import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Row, Spinner2, Text } from 'components';
import { t } from 'i18next';
import { Api, theme } from 'lib';
import { hoursFromMinutes, minutesFromHours } from './campaignLeadTime';
import Section from './Section';
import Toggle from './Toggle';

// Reads the row's label and description from the locale files each time they render, so they
// follow the admin's language rather than the one loaded when this module was first imported.
const localizedMeta = (key, meta) => ({
  ...meta,
  get label() {
    return t(`admin.notifications.${key}.label`);
  },
  get desc() {
    return t(`admin.notifications.${key}.desc`);
  }
});

const NOTIFICATION_META = {
  predictionGameDay: {
    label: 'Predictions — Game Day',
    desc: 'Sent to followers/subscribers on game day that predictions are open.',
    placeholders: ['{match}']
  },
  predictionLastChance: {
    label: 'Predictions — Last Chance',
    desc: 'Sent shortly before kickoff to fans who haven’t predicted yet.',
    placeholders: ['{match}', '{minutes}'],
    minutesLabel: 'Minutes before kickoff'
  },
  matchCountdown12h: {
    label: 'Match — Kickoff Countdown (12h)',
    desc: 'Sent to followers/subscribers 12 hours before kickoff.',
    placeholders: ['{team}', '{home}', '{away}']
  },
  pollOpen: {
    label: 'Poll — Open',
    desc: 'Sent when a new poll becomes available to vote on.',
    placeholders: ['{team}']
  },
  pollClosingSoon: {
    label: 'Poll — Closing Soon',
    desc: 'Sent shortly before a poll’s voting deadline.',
    placeholders: ['{team}'],
    minutesLabel: 'Minutes before deadline'
  },
  quizOpen: {
    label: 'Quick Quiz — Open',
    desc: 'Sent when a Quick Quiz becomes playable.',
    placeholders: ['{team}']
  },
  quizClosingSoon: {
    label: 'Quick Quiz — Closing Soon',
    desc: 'Sent shortly before a daily quiz’s deadline.',
    placeholders: ['{team}'],
    minutesLabel: 'Minutes before deadline'
  },
  shoutoutGameDay: {
    label: 'Shoutout — Game Day',
    desc: 'Sent on game day prompting fans to send a shoutout.',
    placeholders: ['{match}']
  },
  shoutoutWeeklyReset: {
    label: 'Shoutout — Weekly Reset',
    desc: 'Sent every Monday at 00:15 UTC announcing the weekly shoutout quota reset.',
    placeholders: []
  },
  fancamChallengeGameDay: {
    label: 'FanCam Challenge — Game Day',
    desc: 'Sent on game day announcing new FanCam Challenge upload slots.',
    placeholders: ['{match}']
  },
  fanChatLive: {
    label: 'FanChat — Halftime Live',
    desc: 'Sent to subscribers at halftime that FanChat is live.',
    placeholders: ['{match}']
  },
  fanChatTopicFirstResponse: localizedMeta('fanChatTopicFirstResponse', {
    placeholders: ['{topic}', '{user}']
  }),
  fanChatTopicReply: localizedMeta('fanChatTopicReply', { placeholders: ['{topic}', '{user}'] }),
  fanChatTopicDigest: localizedMeta('fanChatTopicDigest', { placeholders: ['{topic}', '{count}'] }),
  fanChatTopicDeleted: localizedMeta('fanChatTopicDeleted', { placeholders: ['{topic}'] }),
  fanwaveLive: {
    label: 'FanWave — Live',
    desc: 'Sent when a team’s FanWave goes live.',
    placeholders: ['{team}']
  },
  fanwaveClosingSoon: {
    label: 'FanWave — Closing Soon',
    desc: 'Sent shortly before a live FanWave closes.',
    placeholders: ['{team}'],
    minutesLabel: 'Minutes before close'
  },
  fanwaveRewardCommunity: {
    label: 'FanWave — Community Bonus Reward',
    desc: 'Sent when a fan earns a FanWave community bonus payout.',
    placeholders: ['{team}', '{point}']
  },
  gloryBoardTierUp: {
    label: 'GloryBoard — Tier Up',
    desc: 'Sent when a fan’s badge tier upgrades (Bronze/Silver/Gold).',
    placeholders: ['{team}', '{tier}']
  },
  gloryBoardHalftimeRanks: {
    label: 'GloryBoard — Halftime Rankings',
    desc: 'Sent at halftime with the top-3 fans by points.',
    placeholders: ['{match}', '{first}', '{second}', '{third}']
  },

  // contentRemoved: {
  //   label: 'Moderation — Content Removed',
  //   desc: 'Sent when a fan’s content is taken down by moderation. Enable/disable only — text isn’t editable here.',
  //   placeholders: [],
  //   textEditable: false
  // },
  // moderationWarning: {
  //   label: 'Moderation — Content Warning',
  //   desc: 'Sent when a fan’s content publishes with a moderation warning. Enable/disable only — text isn’t editable here.',
  //   placeholders: [],
  //   textEditable: false
  // },

  gloryBoardRankUp: {
    label: 'GloryBoard — Rank Up',
    desc: 'Sent daily at 00:00 UTC when a fan’s GloryBoard rank improves (bell only — no push, and no editable text since the app renders its own message).',
    placeholders: [],
    only: 'pop'
  },
  matchEvents: {
    label: 'Match Events (Goals, Cards, Scores, VAR)',
    desc: 'Push + in-app bell notifications for goals, red cards, VAR decisions, HT/FT score, etc. Text editing for these isn’t available yet, enable/disable only.',
    placeholders: [],
    textEditable: false
  },
  gloryBoardPostMatchRanks: {
    label: 'GloryBoard — Final Rankings',
    desc: 'Sent after full-time with the top-3 fans by points.',
    placeholders: ['{match}', '{first}', '{second}', '{third}']
  },
  featureCampaignStartingSoon: {
    label: 'Campaign — Starting Soon',
    desc: 'Sent to followers of the campaign’s teams before it starts. One message per campaign, not per feature.',
    placeholders: ['{name}', '{features}', '{time}'],
    hoursLabel: 'Hours before campaign start'
  },
  featureCampaignStart: {
    label: 'Campaign — Started',
    desc: 'Sent to followers of the campaign’s teams the moment the campaign starts.',
    placeholders: ['{name}', '{features}']
  },
  featureCampaignEndingSoon: {
    label: 'Campaign — Ending Soon',
    desc: 'Sent to followers of the campaign’s teams before it ends.',
    placeholders: ['{name}', '{features}', '{time}'],
    hoursLabel: 'Hours before campaign end'
  }
};

const ROW_ORDER = Object.keys(NOTIFICATION_META);

// Sample values used to render a live "what fans will actually see" preview —
// keyed by the same placeholder names used in {curly} templates.
const SAMPLE_VALUES = {
  team: 'Kashiwa Reysol',
  teamName: 'Kashiwa Reysol',
  match: 'Kashiwa Reysol vs FC Tokyo',
  home: 'Kashiwa Reysol',
  away: 'FC Tokyo',
  point: '50',
  minutes: '15',
  period: 'Half-time',
  rank: '3',
  tier: 'Gold',
  first: 'yuki_23',
  second: 'sakura_m',
  third: 'kenji99',
  name: 'Autumn Fan Fest',
  features: 'FanChat and Shoutouts',
  time: '1 hour',
  topic: 'Who starts up front on Saturday?',
  count: '12',
  user: 'sakura_m'
};

function withSampleValues(template) {
  return (template || '').replace(/\{(\w+)\}/g, (match, key) => SAMPLE_VALUES[key] ?? match);
}

// Hours before the campaign start or end, shown as hours but stored as whole minutes
// (see campaignLeadTime.js). Zero is not allowed: "never" is what the switches are for.
const HoursInput = ({ minutes, disabled, label, onChange }) => {
  const [draft, setDraft] = useState(hoursFromMinutes(minutes));
  const invalid = minutesFromHours(draft) === null;

  // The draft is what is typed, so "0." or an empty box does not jump while editing. It follows
  // the stored value when that changes from outside, e.g. after settings are reloaded.
  useEffect(() => {
    if (minutesFromHours(draft) !== minutes) setDraft(hoursFromMinutes(minutes));
  }, [minutes]);

  return (
    <Row
      centerAll
      gap="6px"
      style={{
        flexShrink: 0,
        backgroundColor: theme.GREY_10 || '#f5f5f6',
        borderRadius: '8px',
        padding: '6px 10px',
        outline: invalid && !disabled ? `1px solid ${theme.SECONDARY || '#e5484d'}` : 'none'
      }}
      title={invalid ? 'Enter a number greater than 0' : label}
    >
      <Text c1 col={theme.GREY_60} style={{ fontSize: '15px', lineHeight: 1 }}>
        ⏱
      </Text>
      <input
        type="number"
        min="0.1"
        step="0.1"
        value={draft}
        disabled={disabled}
        aria-label={label}
        // Leaving the box with nothing valid in it puts the last good value back.
        onBlur={() => setDraft(hoursFromMinutes(minutes))}
        onChange={(e) => {
          setDraft(e.target.value);
          const next = minutesFromHours(e.target.value);
          if (next !== null) onChange(next);
        }}
        style={{
          width: '48px',
          boxSizing: 'border-box',
          border: 'none',
          background: 'transparent',
          textAlign: 'center',
          fontFamily: 'inherit',
          fontSize: '13px',
          fontWeight: 600,
          color: theme.PRIMARY_BLACK || '#111',
          cursor: disabled ? 'not-allowed' : 'text'
        }}
      />
      <Text c1 col={theme.GREY_60} style={{ fontSize: '11px' }}>
        hr
      </Text>
    </Row>
  );
};

const NotificationsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [types, setTypes] = useState([]);
  const [errors, setErrors] = useState({});
  const cardRefs = useRef({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await Api.get('/admin/notificationSettings/get');
      if (res?.data?.settings?.types) {
        setTypes(res.data.settings.types);
      }
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (key, changes) => {
    setTypes((prev) => prev.map((t) => (t.key === key ? { ...t, ...changes } : t)));
  };

  const handlePushToggle = (key) => {
    const row = types.find((t) => t.key === key);
    if (row) updateRow(key, { pushEnabled: !row.pushEnabled });
  };

  const handlePopToggle = (key) => {
    const row = types.find((t) => t.key === key);
    if (row) updateRow(key, { popEnabled: !row.popEnabled });
  };

  const handleTextChange = (key, lang, field, value) => {
    const row = types.find((t) => t.key === key);
    if (row) updateRow(key, { [lang]: { ...row[lang], [field]: value } });

    if (value.trim() && errors[key]?.[lang]?.[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        const rowErrors = { ...next[key], [lang]: { ...next[key][lang], [field]: false } };
        if (
          !rowErrors.en?.title &&
          !rowErrors.en?.body &&
          !rowErrors.ja?.title &&
          !rowErrors.ja?.body
        ) {
          delete next[key];
        } else {
          next[key] = rowErrors;
        }
        return next;
      });
    }
  };

  const handleMinutesChange = (key, value) => {
    const minutes = Math.max(0, Number(value) || 0);
    updateRow(key, { minutes });
  };

  // Builds { [rowKey]: { en: {title, body}, ja: {title, body} } } — each leaf
  // `true` means that field is blank and must be fixed before saving.
  const validateTypes = () => {
    const errorMap = {};
    for (const row of types) {
      const meta = NOTIFICATION_META[row.key];
      const showTextEditor = meta && meta.only !== 'pop' && meta.textEditable !== false;
      const isActive = !!row.pushEnabled || !!row.popEnabled;
      // Disabled (both toggles off) rows have their inputs locked, so an
      // unfilled field there can never be fixed — and can never send blank
      // either, since neither channel is on. Skip validating them.
      if (!showTextEditor || !isActive) continue;

      const rowErrors = {};
      for (const { key: lang } of LANG_TABS) {
        const values = row[lang] || {};
        const titleMissing = !values.title?.trim();
        const bodyMissing = !values.body?.trim();
        if (titleMissing || bodyMissing) {
          rowErrors[lang] = { title: titleMissing, body: bodyMissing };
        }
      }
      if (Object.keys(rowErrors).length > 0) {
        errorMap[row.key] = rowErrors;
      }
    }
    return errorMap;
  };

  const handleSave = async () => {
    const badLead = types.find(
      (row) => NOTIFICATION_META[row.key]?.hoursLabel && !(row.minutes >= 1)
    );
    if (badLead) {
      alert(`${NOTIFICATION_META[badLead.key].label}: enter a number of hours greater than 0.`);
      cardRefs.current[badLead.key]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const errorMap = validateTypes();
    if (Object.keys(errorMap).length > 0) {
      setErrors(errorMap);
      const firstInvalidKey = ROW_ORDER.find((key) => errorMap[key]);
      cardRefs.current[firstInvalidKey]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setErrors({});

    try {
      setSaving(true);
      await Api.put('/admin/notificationSettings/update', { types });
      alert('Settings updated successfully');
      fetchSettings();
    } catch (error) {
      console.error('Error updating notification settings:', error);
      alert('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Col wid="100%" ht="100vh" centerAll noFlex>
        <Spinner2 />
      </Col>
    );
  }

  const rows = ROW_ORDER.map((key) => types.find((t) => t.key === key)).filter(Boolean);

  return (
    <Col start noFlex wid="100%" pad="24px" gap="24px">
      <Section
        title="Notifications"
        description="Turn each notification on or off, and edit its English/Japanese title + body. Placeholders shown per row are substituted automatically when the notification is sent."
      >
        <Col
          wid="100%"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}
        >
          {rows.map((row) => (
            <NotificationCard
              key={row.key}
              ref={(el) => {
                cardRefs.current[row.key] = el;
              }}
              row={row}
              meta={NOTIFICATION_META[row.key]}
              errors={errors[row.key]}
              onPushToggle={() => handlePushToggle(row.key)}
              onPopToggle={() => handlePopToggle(row.key)}
              onMinutesChange={(value) => handleMinutesChange(row.key, value)}
              onTextChange={(lang, field, value) => handleTextChange(row.key, lang, field, value)}
            />
          ))}
        </Col>
      </Section>

      <Row marg="0 0 48px" centerAll>
        <Button
          text={saving ? 'Updating...' : 'Save Changes'}
          onClick={handleSave}
          bg={theme.PRIMARY}
          loading={saving}
          wid="200px"
        />
      </Row>
    </Col>
  );
};

const LANG_TABS = [
  { key: 'en', label: 'EN' },
  { key: 'ja', label: '日本語' }
];

const ERROR_COLOR = '#e02424';

const NotificationCard = React.forwardRef(
  ({ row, meta, errors, onPushToggle, onPopToggle, onMinutesChange, onTextChange }, ref) => {
    const [lang, setLang] = useState('en');
    const values = row[lang];
    const isActive = !!row.pushEnabled || !!row.popEnabled;
    // Title/body text is only ever consumed by the push send — the in-app bell
    // renders from raw data fields, not this text — so a pop-only type (never
    // pushes) has nothing meaningful to edit here. `textEditable: false` covers
    // types whose text still comes from the older i18next system either way.
    const showTextEditor = meta.only !== 'pop' && meta.textEditable !== false;
    const langErrors = errors?.[lang];
    const titleError = !!langErrors?.title;
    const bodyError = !!langErrors?.body;

    // A failed field can be hidden behind the other language's tab — jump to
    // whichever language actually has the error so the red border is visible.
    useEffect(() => {
      if (!errors) return;
      if (errors[lang]) return;
      const otherLang = LANG_TABS.map((t) => t.key).find((key) => key !== lang && errors[key]);
      if (otherLang) setLang(otherLang);
    }, [errors]);

    return (
      <Col
        ref={ref}
        wid="100%"
        style={{
          borderRadius: '12px',
          border: `1px solid ${errors ? ERROR_COLOR : theme.GREY_20 || '#eaeaea'}`,
          backgroundColor: '#fff',
          boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
          opacity: isActive ? 1 : 0.5,
          transition: 'opacity 0.15s ease, border-color 0.15s ease'
        }}
      >
        <Row center pad="16px 18px" style={{ gap: '14px' }}>
          <Col style={{ flex: 1, minWidth: 0 }}>
            <Text b2 bold marg="0">
              {meta.label}
            </Text>
            <Text
              c1
              col={theme.GREY_60}
              marg="2px 0 0"
              style={{ fontSize: '11.5px', lineHeight: 1.4 }}
            >
              {meta.desc}
            </Text>
          </Col>

          {meta.minutesLabel && (
            <Row
              centerAll
              gap="6px"
              style={{
                flexShrink: 0,
                backgroundColor: theme.GREY_10 || '#f5f5f6',
                borderRadius: '8px',
                padding: '6px 10px'
              }}
              title={meta.minutesLabel}
            >
              <Text c1 col={theme.GREY_60} style={{ fontSize: '15px', lineHeight: 1 }}>
                ⏱
              </Text>
              <input
                type="number"
                min="0"
                value={row.minutes ?? 0}
                disabled={!isActive}
                onChange={(e) => onMinutesChange(e.target.value)}
                style={{
                  width: '34px',
                  boxSizing: 'border-box',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'center',
                  fontFamily: 'inherit',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: theme.PRIMARY_BLACK || '#111',
                  cursor: isActive ? 'text' : 'not-allowed'
                }}
              />
              <Text c1 col={theme.GREY_60} style={{ fontSize: '11px' }}>
                min
              </Text>
            </Row>
          )}

          {meta.hoursLabel && (
            <HoursInput
              minutes={row.minutes}
              disabled={!isActive}
              label={meta.hoursLabel}
              onChange={onMinutesChange}
            />
          )}

          {showTextEditor && <LangSwitch lang={lang} onChange={setLang} errors={errors} />}

          <ChannelToggles
            row={row}
            only={meta.only}
            onPushToggle={onPushToggle}
            onPopToggle={onPopToggle}
          />
        </Row>

        {showTextEditor && (
          <Col
            gap="8px"
            pad="14px 18px 16px"
            style={{ borderTop: `1px solid ${theme.GREY_10 || '#f2f2f2'}`, width: '100%' }}
          >
            <input
              type="text"
              value={values?.title || ''}
              placeholder="Title"
              disabled={!isActive}
              onChange={(e) => onTextChange(lang, 'title', e.target.value)}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 10px',
                borderRadius: '7px',
                border: `1px solid ${titleError ? ERROR_COLOR : theme.GREY_20 || '#ddd'}`,
                backgroundColor: isActive ? '#fff' : theme.GREY_10 || '#f5f5f6',
                cursor: isActive ? 'text' : 'not-allowed',
                fontFamily: 'inherit',
                fontSize: '13px',
                fontWeight: 600
              }}
            />
            <textarea
              value={values?.body || ''}
              placeholder="Body"
              rows={1}
              disabled={!isActive}
              onChange={(e) => onTextChange(lang, 'body', e.target.value)}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 10px',
                borderRadius: '7px',
                border: `1px solid ${bodyError ? ERROR_COLOR : theme.GREY_20 || '#ddd'}`,
                backgroundColor: isActive ? '#fff' : theme.GREY_10 || '#f5f5f6',
                cursor: isActive ? 'text' : 'not-allowed',
                fontFamily: 'inherit',
                fontSize: '13px',
                lineHeight: '1.4',
                resize: 'vertical'
              }}
            />
            {(titleError || bodyError) && (
              <Text c1 style={{ fontSize: '11px', color: ERROR_COLOR }}>
                Title and body are both required — push notifications will show a blank message
                otherwise.
              </Text>
            )}

            {meta.placeholders.length > 0 && (
              <Row style={{ flexWrap: 'wrap', gap: '6px' }}>
                <Text c1 col={theme.GREY_60} style={{ fontSize: '10.5px', marginRight: '2px' }}>
                  Insert:
                </Text>
                {meta.placeholders.map((p) => (
                  <Text
                    key={p}
                    c1
                    style={{
                      fontSize: '10.5px',
                      fontFamily: 'monospace',
                      color: theme.PRIMARY || '#333',
                      backgroundColor: theme.GREY_10 || '#f2f2f2',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}
                  >
                    {p}
                  </Text>
                ))}
              </Row>
            )}

            <Col
              gap="2px"
              style={{
                backgroundColor: theme.GREY_10 || '#f5f5f6',
                borderRadius: '7px',
                padding: '8px 10px'
              }}
            >
              <Text
                c1
                col={theme.GREY_60}
                style={{ fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '0.05em' }}
              >
                Preview
              </Text>
              <Text b2 bold marg="0" style={{ fontSize: '12.5px' }}>
                {withSampleValues(values?.title) || '—'}
              </Text>
              <Text c1 col={theme.GREY_60} style={{ fontSize: '12px', lineHeight: 1.4 }}>
                {withSampleValues(values?.body) || '—'}
              </Text>
            </Col>
          </Col>
        )}
      </Col>
    );
  }
);

const ChannelToggles = ({ row, only, onPushToggle, onPopToggle }) => (
  <Row gap="12px" style={{ flexShrink: 0 }}>
    {only !== 'pop' && (
      <Col centerAll gap="3px">
        <Text
          c1
          col={theme.GREY_60}
          style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.03em' }}
        >
          Push
        </Text>
        <Toggle on={!!row.pushEnabled} onToggle={onPushToggle} />
      </Col>
    )}
    {only !== 'push' && (
      <Col centerAll gap="3px">
        <Text
          c1
          col={theme.GREY_60}
          style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.03em' }}
        >
          Pop
        </Text>
        <Toggle on={!!row.popEnabled} onToggle={onPopToggle} />
      </Col>
    )}
  </Row>
);

const LangSwitch = ({ lang, onChange, errors }) => (
  <Row
    style={{
      flexShrink: 0,
      backgroundColor: theme.GREY_10 || '#f2f2f2',
      borderRadius: '8px',
      padding: '2px'
    }}
  >
    {LANG_TABS.map((tab) => {
      const tabError = errors?.[tab.key];
      const hasError = !!(tabError?.title || tabError?.body);
      return (
        <button
          key={tab.key}
          type="button"
          onClick={() => onChange(tab.key)}
          style={{
            position: 'relative',
            border: 'none',
            cursor: 'pointer',
            padding: '5px 10px',
            borderRadius: '6px',
            fontFamily: 'inherit',
            fontSize: '11px',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            backgroundColor: lang === tab.key ? '#fff' : 'transparent',
            color: lang === tab.key ? theme.PRIMARY_BLACK || '#111' : theme.GREY_60 || '#888',
            boxShadow: lang === tab.key ? '0 1px 2px rgba(16, 24, 40, 0.12)' : 'none',
            transition: 'all 0.15s ease'
          }}
        >
          {tab.label}
          {hasError && (
            <span
              style={{
                position: 'absolute',
                top: '2px',
                right: '2px',
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: ERROR_COLOR
              }}
            />
          )}
        </button>
      );
    })}
  </Row>
);

export default NotificationsTab;
